import LocalizedMessage from '../common/LocalizedMessage';
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import SupportReportingSetting from "../support/SupportReportingSetting";
import SettingsService from "../../services/SettingsService";
import SchoolCalendarSettingsSummary from "../schoolCalendar/SchoolCalendarSettingsSummary";
import {
  DEFAULT_LESSON_TYPES_SETTING,
  generateClassTypeId,
  normalizeLessonTypesSetting,
} from "../common/lessonTypes";
import { useTranslation } from 'react-i18next';

const DEFAULT_PAYMENT_REMINDER_RULES = {
  id: "paymentReminderRules",
  enabled: true,
  firstOverdueReminderDaysAfterDueDate: 2,
  nextOverdueReminderDaysAfterPrevious: 7,
  maxOverdueReminders: 3,
};

const DEFAULT_SCHOOL_CONTACT = {
  id: "schoolContact",
  email: "szkola@extra-english.pl",
  phone: "(+48) 575 400 141",
  address: "Wypoczynkowa 43, 62-081 Baranowo",
  mapsUrl: "https://www.google.com/maps/search/?api=1&query=Extra%20English%20Wypoczynkowa%2043%2062-081%20Baranowo",
};

const DEFAULT_CANCELLATION_POLICY = {
  id: "cancellationPolicy",
  enabled: true,
  cancellationCutoffHours: 24,
};

const DEFAULT_DASHBOARD_PREFERENCES = {
  id: "dashboardPreferences",
  returnStateTtlHours: 6,
};

const DEFAULT_PACKAGE_POLICY = {
  id: "packagePolicy",
  pendingPaymentDeadlineDays: 7,
  pendingPaymentReminderDaysBeforeDue: 2,
  endingSoonThresholdUnits: 2,
  emailStudentOnEndingPackage: false,
};

function byId(settings, id, fallback) {
  return settings.find(setting => setting.id === id) || fallback;
}

function toPositiveInputValue(value) {
  return Number.isInteger(Number(value)) && Number(value) > 0 ? Number(value) : 1;
}

const DEFAULT_NEW_LESSON_TYPE = {
  category: "I",
  durationMinutes: 90,
  name: "",
  minStudents: 1,
  maxStudents: 1,
};

const Settings = ({ user, calendarContext, schoolYearStart }) => {
  const { t: tr } = useTranslation();
  const [paymentRules, setPaymentRules] = useState(DEFAULT_PAYMENT_REMINDER_RULES);
  const [schoolContact, setSchoolContact] = useState(DEFAULT_SCHOOL_CONTACT);
  const [cancellationPolicy, setCancellationPolicy] = useState(DEFAULT_CANCELLATION_POLICY);
  const [dashboardPreferences, setDashboardPreferences] = useState(DEFAULT_DASHBOARD_PREFERENCES);
  const [packagePolicy, setPackagePolicy] = useState(DEFAULT_PACKAGE_POLICY);
  const [lessonTypes, setLessonTypes] = useState(DEFAULT_LESSON_TYPES_SETTING);
  const [newLessonType, setNewLessonType] = useState(DEFAULT_NEW_LESSON_TYPE);
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    SettingsService.getSettings()
      .then((settings) => {
        setPaymentRules(byId(settings, "paymentReminderRules", DEFAULT_PAYMENT_REMINDER_RULES));
        setSchoolContact(byId(settings, "schoolContact", DEFAULT_SCHOOL_CONTACT));
        setCancellationPolicy(byId(settings, "cancellationPolicy", DEFAULT_CANCELLATION_POLICY));
        setDashboardPreferences(byId(settings, "dashboardPreferences", DEFAULT_DASHBOARD_PREFERENCES));
        setPackagePolicy(byId(settings, "packagePolicy", DEFAULT_PACKAGE_POLICY));
        setLessonTypes(normalizeLessonTypesSetting(byId(settings, "lessonTypes", DEFAULT_LESSON_TYPES_SETTING)));
        setErrorMessage("");
      })
      .catch(() => {
        setErrorMessage(tr("manage:extraCouldNotLoadSettings_79"));
      })
      .finally(() => setLoading(false));
  }, []);

  const handlePaymentChange = (event) => {
    const { name, value, type, checked } = event.target;
    setPaymentRules({
      ...paymentRules,
      [name]: type === "checkbox" ? checked : toPositiveInputValue(value),
    });
  };

  const handleSchoolContactChange = (event) => {
    const { name, value } = event.target;
    setSchoolContact({
      ...schoolContact,
      [name]: value,
    });
  };

  const handleCancellationPolicyChange = (event) => {
    const { name, value, type, checked } = event.target;
    setCancellationPolicy({
      ...cancellationPolicy,
      [name]: type === "checkbox" ? checked : toPositiveInputValue(value),
    });
  };

  const handleDashboardPreferencesChange = (event) => {
    const { name, value } = event.target;
    setDashboardPreferences({
      ...dashboardPreferences,
      [name]: toPositiveInputValue(value),
    });
  };

  const handlePackagePolicyChange = (event) => {
    const { name, value, type, checked } = event.target;
    setPackagePolicy({
      ...packagePolicy,
      [name]: type === "checkbox" ? checked : toPositiveInputValue(value),
    });
  };

  const handleLessonTypeChange = (index, field, value) => {
    setLessonTypes({
      ...lessonTypes,
      types: lessonTypes.types.map((type, currentIndex) => {
        if (currentIndex !== index) return type;
        if (field === "status") return { ...type, status: value ? "ACTIVE" : "INACTIVE" };
        if (["durationMinutes", "minStudents", "maxStudents"].includes(field)) return { ...type, [field]: toPositiveInputValue(value) };
        return { ...type, [field]: value };
      }),
    });
  };

  const handleNewLessonTypeChange = (event) => {
    const { name, value } = event.target;
    setNewLessonType({
      ...newLessonType,
      [name]: ["durationMinutes", "minStudents", "maxStudents"].includes(name)
        ? toPositiveInputValue(value)
        : value,
    });
  };

  const getNewLessonTypeId = () => {
    try {
      return generateClassTypeId(newLessonType.category, newLessonType.durationMinutes);
    } catch (error) {
      return "";
    }
  };

  const addLessonType = () => {
    const classTypeId = getNewLessonTypeId();
    if (!classTypeId) {
      setErrorMessage(tr("manage:extraInvalidLessonTypeCode_80"));
      return;
    }
    if (lessonTypes.types.some(type => type.classTypeId === classTypeId)) {
      setErrorMessage(tr('common:duplicateLessonType', { id: classTypeId }));
      return;
    }

    const lessonType = {
      classTypeId,
      category: newLessonType.category,
      name: newLessonType.name || classTypeId,
      durationMinutes: newLessonType.durationMinutes,
      minStudents: newLessonType.minStudents,
      maxStudents: newLessonType.maxStudents,
      status: "ACTIVE",
    };

    setLessonTypes(normalizeLessonTypesSetting({
      ...lessonTypes,
      types: [...lessonTypes.types, lessonType],
    }));
    setNewLessonType(DEFAULT_NEW_LESSON_TYPE);
    setErrorMessage("");
  };

  const save = (sectionName, setting, successText) => {
    setSavingSection(sectionName);
    setSuccessMessage("");
    setErrorMessage("");
    return SettingsService.saveSetting(setting)
      .then((savedSetting) => {
        if (savedSetting.id === "paymentReminderRules") setPaymentRules(savedSetting);
        if (savedSetting.id === "schoolContact") setSchoolContact(savedSetting);
        if (savedSetting.id === "cancellationPolicy") setCancellationPolicy(savedSetting);
        if (savedSetting.id === "dashboardPreferences") setDashboardPreferences(savedSetting);
        if (savedSetting.id === "packagePolicy") setPackagePolicy(savedSetting);
        if (savedSetting.id === "lessonTypes") setLessonTypes(normalizeLessonTypesSetting(savedSetting));
        setSuccessMessage(successText);
      })
      .catch(() => {
        setErrorMessage(tr("manage:extraCouldNotSaveSettings_81"));
      })
      .finally(() => setSavingSection(""));
  };

  if (loading) {
    return <div className="manage-text">{tr("manage:loadingSettings_311")}</div>;
  }

  return (
    <div>
      <h2 className="manage-title">{tr("common:settings")}</h2>
      {user && (user.role === "ADMIN" || (user.systemRoles || []).includes("ADMIN") || (user.platformRoles || []).includes("SUPER_ADMIN")) && <SupportReportingSetting key={user.schoolId} />}
      {calendarContext && schoolYearStart && <SchoolCalendarSettingsSummary contextKey={calendarContext} schoolYearStart={schoolYearStart} />}

      {successMessage && <div className="alert alert-success" role="alert"><LocalizedMessage value={successMessage} /></div>}
      {errorMessage && <div className="alert alert-danger" role="alert"><LocalizedMessage isError value={errorMessage} /></div>}

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">{tr("manage:paymentReminders_120")}</h5>
          <div className="form-group form-check">
            <input
              id="payment-reminders-enabled"
              className="form-check-input"
              type="checkbox"
              name="enabled"
              checked={paymentRules.enabled}
              onChange={handlePaymentChange}
            />
            <label className="form-check-label" htmlFor="payment-reminders-enabled">{tr("manage:enableReminders_312")}</label>
          </div>
          <div className="form-row">
            <div className="form-group col-md-4">
              <label htmlFor="firstOverdueReminderDaysAfterDueDate">{tr("manage:firstReminderAfterDueDate_313")}</label>
              <input
                id="firstOverdueReminderDaysAfterDueDate"
                className="form-control"
                type="number"
                min="1"
                name="firstOverdueReminderDaysAfterDueDate"
                value={paymentRules.firstOverdueReminderDaysAfterDueDate}
                onChange={handlePaymentChange}
              />
            </div>
            <div className="form-group col-md-4">
              <label htmlFor="nextOverdueReminderDaysAfterPrevious">{tr("manage:repeatRemindersEveryDays_314")}</label>
              <input
                id="nextOverdueReminderDaysAfterPrevious"
                className="form-control"
                type="number"
                min="1"
                name="nextOverdueReminderDaysAfterPrevious"
                value={paymentRules.nextOverdueReminderDaysAfterPrevious}
                onChange={handlePaymentChange}
              />
            </div>
            <div className="form-group col-md-4">
              <label htmlFor="maxOverdueReminders">{tr("manage:maximumNumberOfReminders_315")}</label>
              <input
                id="maxOverdueReminders"
                className="form-control"
                type="number"
                min="1"
                name="maxOverdueReminders"
                value={paymentRules.maxOverdueReminders}
                onChange={handlePaymentChange}
              />
            </div>
          </div>
          <button
            type="button"
            className="btn manage-button manage-red-button"
            disabled={savingSection === "payment"}
            onClick={() => save("payment", paymentRules, tr("manage:extraReminderSettingsSaved_82"))}
          >{tr("manage:saveReminders_316")}</button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">{tr("manage:schoolContact_317")}</h5>
          <div className="form-row">
            <div className="form-group col-md-6">
              <label htmlFor="schoolContactEmail">{tr("common:email")}</label>
              <input
                id="schoolContactEmail"
                className="form-control"
                type="email"
                name="email"
                value={schoolContact.email}
                onChange={handleSchoolContactChange}
              />
            </div>
            <div className="form-group col-md-6">
              <label htmlFor="schoolContactPhone">{tr("common:phone")}</label>
              <input
                id="schoolContactPhone"
                className="form-control"
                type="text"
                name="phone"
                value={schoolContact.phone}
                onChange={handleSchoolContactChange}
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="schoolContactAddress">{tr("manage:address_124")}</label>
            <input
              id="schoolContactAddress"
              className="form-control"
              type="text"
              name="address"
              value={schoolContact.address}
              onChange={handleSchoolContactChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="schoolContactMapsUrl">{tr("manage:mapLink_318")}</label>
            <input
              id="schoolContactMapsUrl"
              className="form-control"
              type="text"
              name="mapsUrl"
              value={schoolContact.mapsUrl}
              onChange={handleSchoolContactChange}
            />
          </div>
          <button
            type="button"
            className="btn manage-button manage-red-button"
            disabled={savingSection === "schoolContact"}
            onClick={() => save("schoolContact", schoolContact, tr("manage:extraContactDetailsSaved_83"))}
          >{tr("manage:saveContact_319")}</button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">{tr("manage:lessonCancellationPolicy_320")}</h5>
          <div className="form-group form-check">
            <input
              id="cancellation-policy-enabled"
              className="form-check-input"
              type="checkbox"
              name="enabled"
              checked={cancellationPolicy.enabled}
              onChange={handleCancellationPolicyChange}
            />
            <label className="form-check-label" htmlFor="cancellation-policy-enabled">{tr("manage:enableCancellationPolicy_321")}</label>
          </div>
          <div className="form-group">
            <label htmlFor="cancellationCutoffHours">{tr("manage:freeCancellationUpToHoursBeforeLesson_322")}</label>
            <input
              id="cancellationCutoffHours"
              className="form-control"
              type="number"
              min="1"
              name="cancellationCutoffHours"
              value={cancellationPolicy.cancellationCutoffHours}
              onChange={handleCancellationPolicyChange}
            />
          </div>
          <button
            type="button"
            className="btn manage-button manage-red-button"
            disabled={savingSection === "cancellationPolicy"}
            onClick={() => save("cancellationPolicy", cancellationPolicy, tr("manage:extraCancellationPolicySaved_84"))}
          >{tr("manage:saveCancellationPolicy_323")}</button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">{tr("manage:dashboard_324")}</h5>
          <div className="form-group">
            <label htmlFor="returnStateTtlHours">{tr("manage:dashboardMemoryInHours_325")}</label>
            <input
              id="returnStateTtlHours"
              className="form-control"
              type="number"
              min="1"
              name="returnStateTtlHours"
              value={dashboardPreferences.returnStateTtlHours}
              onChange={handleDashboardPreferencesChange}
            />
          </div>
          <button
            type="button"
            className="btn manage-button manage-red-button"
            disabled={savingSection === "dashboardPreferences"}
            onClick={() => save("dashboardPreferences", dashboardPreferences, tr("manage:extraDashboardSettingsSaved_85"))}
          >{tr("manage:saveDashboardSettings_326")}</button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">{tr("manage:packages_327")}</h5>
          <div className="form-row">
            <div className="form-group col-md-4">
              <label htmlFor="pendingPaymentDeadlineDays">{tr("manage:daysAPackageWaitsForPayment_328")}<input
                  id="pendingPaymentDeadlineDays"
                  className="form-control"
                  type="number"
                  min="1"
                  name="pendingPaymentDeadlineDays"
                  value={packagePolicy.pendingPaymentDeadlineDays}
                  onChange={handlePackagePolicyChange}
                />
              </label>
            </div>
            <div className="form-group col-md-4">
              <label htmlFor="pendingPaymentReminderDaysBeforeDue">{tr("manage:reminderDaysBeforeDeletion_329")}<input
                  id="pendingPaymentReminderDaysBeforeDue"
                  className="form-control"
                  type="number"
                  min="1"
                  name="pendingPaymentReminderDaysBeforeDue"
                  value={packagePolicy.pendingPaymentReminderDaysBeforeDue}
                  onChange={handlePackagePolicyChange}
                />
              </label>
            </div>
            <div className="form-group col-md-4">
              <label htmlFor="endingSoonThresholdUnits">{tr("manage:packageRunninglowThreshold_330")}<input
                  id="endingSoonThresholdUnits"
                  className="form-control"
                  type="number"
                  min="1"
                  name="endingSoonThresholdUnits"
                  value={packagePolicy.endingSoonThresholdUnits}
                  onChange={handlePackagePolicyChange}
                />
              </label>
            </div>
          </div>
          <div className="form-group form-check">
            <label className="form-check-label" htmlFor="emailStudentOnEndingPackage">
              <input
                id="emailStudentOnEndingPackage"
                className="form-check-input"
                type="checkbox"
                name="emailStudentOnEndingPackage"
                checked={packagePolicy.emailStudentOnEndingPackage}
                onChange={handlePackagePolicyChange}
              />{tr("manage:alsoNotifyTheStudent_331")}</label>
          </div>
          <button
            type="button"
            className="btn manage-button manage-red-button"
            disabled={savingSection === "packagePolicy"}
            onClick={() => save("packagePolicy", packagePolicy, tr("manage:extraPackageSettingsSaved_86"))}
          >{tr("manage:savePackageSettings_332")}</button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">{tr("manage:lessonTypes_333")}</h5>
          {lessonTypes.types.map((lessonType, index) => (
            <div className="form-row align-items-end" key={lessonType.classTypeId}>
              <div className="form-group col-md-2">
                <label htmlFor={`lesson-type-${lessonType.classTypeId}-id`}>{tr("manage:code_334")}</label>
                <input
                  id={`lesson-type-${lessonType.classTypeId}-id`}
                  className="form-control"
                  type="text"
                  value={lessonType.classTypeId}
                  readOnly
                />
              </div>
              <div className="form-group col-md-4">
                <label htmlFor={`lesson-type-${lessonType.classTypeId}-name`}>{tr("manage:name_281")}</label>
                <input
                  id={`lesson-type-${lessonType.classTypeId}-name`}
                  className="form-control"
                  type="text"
                  value={lessonType.name}
                  onChange={event => handleLessonTypeChange(index, "name", event.target.value)}
                />
              </div>
              <div className="form-group col-md-2">
                <label htmlFor={`lesson-type-${lessonType.classTypeId}-durationMinutes`}>{tr("manage:minutes_335")}</label>
                <input
                  id={`lesson-type-${lessonType.classTypeId}-durationMinutes`}
                  className="form-control"
                  type="number"
                  min="1"
                  value={lessonType.durationMinutes}
                  readOnly
                />
              </div>
              <div className="form-group col-md-1">
                <label htmlFor={`lesson-type-${lessonType.classTypeId}-min`}>{tr("manage:min_336")}</label>
                <input
                  id={`lesson-type-${lessonType.classTypeId}-min`}
                  className="form-control"
                  type="number"
                  min="1"
                  value={lessonType.minStudents}
                  onChange={event => handleLessonTypeChange(index, "minStudents", event.target.value)}
                />
              </div>
              <div className="form-group col-md-1">
                <label htmlFor={`lesson-type-${lessonType.classTypeId}-max`}>{tr("manage:max_337")}</label>
                <input
                  id={`lesson-type-${lessonType.classTypeId}-max`}
                  className="form-control"
                  type="number"
                  min="1"
                  value={lessonType.maxStudents}
                  onChange={event => handleLessonTypeChange(index, "maxStudents", event.target.value)}
                />
              </div>
              <div className="form-group col-md-2 form-check">
                <input
                  id={`lesson-type-${lessonType.classTypeId}-active`}
                  name={`lessonTypeStatus-${lessonType.classTypeId}`}
                  className="form-check-input"
                  type="checkbox"
                  checked={lessonType.status === "ACTIVE"}
                  onChange={event => handleLessonTypeChange(index, "status", event.target.checked)}
                />
                <label className="form-check-label" htmlFor={`lesson-type-${lessonType.classTypeId}-active`}>{tr("manage:active_338")} {lessonType.classTypeId}
                </label>
              </div>
            </div>
          ))}
          <div className="border-top pt-3 mt-2">
            <div className="form-row align-items-end">
              <div className="form-group col-md-2">
                <label htmlFor="newLessonTypeCategory">{tr("manage:newLessonTypeCategory_339")}</label>
                <select
                  id="newLessonTypeCategory"
                  name="category"
                  className="form-control"
                  value={newLessonType.category}
                  onChange={handleNewLessonTypeChange}
                >
                  <option value="G">G</option>
                  <option value="I">I</option>
                  <option value="D">D</option>
                  <option value="T">T</option>
                  <option value="O">O</option>
                </select>
              </div>
              <div className="form-group col-md-2">
                <label htmlFor="newLessonTypeDurationMinutes">{tr("manage:newLessonTypeDuration_344")}</label>
                <input
                  id="newLessonTypeDurationMinutes"
                  name="durationMinutes"
                  className="form-control"
                  type="number"
                  min="1"
                  value={newLessonType.durationMinutes}
                  onChange={handleNewLessonTypeChange}
                />
              </div>
              <div className="form-group col-md-2">
                <label htmlFor="newLessonTypeId">{tr("manage:code_334")}</label>
                <input
                  id="newLessonTypeId"
                  className="form-control"
                  type="text"
                  value={getNewLessonTypeId()}
                  readOnly
                />
              </div>
              <div className="form-group col-md-3">
                <label htmlFor="newLessonTypeName">{tr("manage:newLessonTypeName_345")}</label>
                <input
                  id="newLessonTypeName"
                  name="name"
                  className="form-control"
                  type="text"
                  value={newLessonType.name}
                  onChange={handleNewLessonTypeChange}
                />
              </div>
              <div className="form-group col-md-1">
                <label htmlFor="newLessonTypeMinStudents">{tr("manage:min_336")}</label>
                <input
                  id="newLessonTypeMinStudents"
                  name="minStudents"
                  className="form-control"
                  type="number"
                  min="1"
                  value={newLessonType.minStudents}
                  onChange={handleNewLessonTypeChange}
                />
              </div>
              <div className="form-group col-md-1">
                <label htmlFor="newLessonTypeMaxStudents">{tr("manage:max_337")}</label>
                <input
                  id="newLessonTypeMaxStudents"
                  name="maxStudents"
                  className="form-control"
                  type="number"
                  min="1"
                  value={newLessonType.maxStudents}
                  onChange={handleNewLessonTypeChange}
                />
              </div>
              <div className="form-group col-md-1">
                <button
                  type="button"
                  className="btn manage-button manage-teal-button btn-block"
                  onClick={addLessonType}
                >{tr("manage:addLessonType_346")}</button>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="btn manage-button manage-red-button"
            disabled={savingSection === "lessonTypes"}
            onClick={() => save("lessonTypes", lessonTypes, tr("manage:extraLessonTypesSaved_87"))}
          >{tr("manage:saveLessonTypes_347")}</button>
        </div>
      </div>
    </div>
  );
};

Settings.propTypes = { user: PropTypes.object, calendarContext: PropTypes.string, schoolYearStart: PropTypes.number };
Settings.defaultProps = { user: null, calendarContext: "", schoolYearStart: null };
export default Settings;
